import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { UserPlus, Check, X } from 'lucide-react';

const CreateAdminPage = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const createAdminUser = async () => {
    if (!supabase) {
      setResult({ success: false, message: 'Supabase client not available' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Step 1: Create user with signup
      console.log('Creating user in Supabase Auth...');
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: 'george.mcpherson@rightawaygroup.com',
        password: 'RahsMatch2024!',
        options: {
          data: {
            full_name: 'George McPherson'
          }
        }
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setResult({
            success: true,
            message: 'User already exists in auth system. Try logging in now!'
          });
          return;
        }
        throw signUpError;
      }

      console.log('Auth user created:', signUpData);

      // Step 2: Create or update app_users record
      if (signUpData.user) {
        console.log('Creating/updating app_users table...');
        const { error: upsertError } = await supabase
          .from('app_users')
          .upsert({
            email: 'george.mcpherson@rightawaygroup.com',
            full_name: 'George McPherson',
            role: 'admin'
          });

        if (upsertError) {
          console.warn('Could not upsert app_users:', upsertError);
        }
      }

      setResult({
        success: true,
        message: 'Admin user created successfully! You can now login.'
      });

    } catch (error: any) {
      console.error('Error creating admin user:', error);
      setResult({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">👤 Create Admin User</h1>

        <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
          <div className="text-center mb-6">
            <UserPlus className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h2 className="text-xl font-semibold mb-2">Create Admin Account</h2>
            <p className="text-slate-400">
              This will create the admin user in Supabase Auth system
            </p>
          </div>

          <div className="bg-slate-800 rounded p-4 mb-6">
            <h3 className="font-semibold mb-2">User Details:</h3>
            <ul className="text-sm text-slate-300 space-y-1">
              <li><strong>Email:</strong> george.mcpherson@rightawaygroup.com</li>
              <li><strong>Password:</strong> [Set during account creation]</li>
              <li><strong>Role:</strong> admin</li>
            </ul>
          </div>

          {result && (
            <div className={`rounded p-4 mb-6 ${
              result.success
                ? 'bg-green-900/20 border border-green-700'
                : 'bg-red-900/20 border border-red-700'
            }`}>
              <div className="flex items-center gap-2">
                {result.success ? (
                  <Check className="w-5 h-5 text-green-400" />
                ) : (
                  <X className="w-5 h-5 text-red-400" />
                )}
                <p className={result.success ? 'text-green-100' : 'text-red-100'}>
                  {result.message}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={createAdminUser}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors"
            >
              {loading ? (
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <UserPlus className="w-5 h-5" />
              )}
              {loading ? 'Creating...' : 'Create Admin User'}
            </button>

            <a
              href="/admin/login"
              className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition-colors"
            >
              Go to Login
            </a>
          </div>
        </div>

        <div className="text-center mt-6">
          <a
            href="/admin/test"
            className="text-slate-400 hover:text-slate-300 transition-colors"
          >
            ← Back to Debug Tests
          </a>
        </div>
      </div>
    </div>
  );
};

export default CreateAdminPage;